/* eslint-disable max-lines */
import { getCurrentOnboardingVariant } from 'helpers/getCurrentOnboardingVariant'
import { getMobileOperatingSystem } from 'helpers/getMobileOperatingSystem'

import {
  PaymentMethod,
  PaymentSystem,
  SourceStep,
} from 'modules/purchase/constants'

import { IEvent, IEventLogger, TQuestionPageParams } from 'models/events.model'
import { TProductId } from 'models/subscriptions.model'

import {
  EventLoggerInstanceName,
  ScreenName,
  SubscriptionListType,
  TimeInterval,
} from 'root-constants'

export const enum Events {
  SESSION_STARTED = 'session_start',
  FIRST_PAGE_SHOW = 'first_page_show',
  FIRST_PAGE_COMPLETED = 'first_page_completed',
  AB_SEGMENT = 'ab_segment',
  QUESTION_COMPLETED = 'question_page_completed',
  BI_USER_AGE = 'bi_user_age',
  BI_USER_GENDER = 'bi_user_gender',
  SALE_SCREEN_SHOW = 'plans_page_show',
  TERMS_OF_USE = 'terms_of_use_tap',
  PRIVACY_POLICY = 'privacy_policy_tap',
  SUBSCRIPTION_CONSENT_ACCEPTED = 'subscription_consent_accepted',
  COOKIE_POLICY_TAP = 'cookies_policy_button_tap',
  PURCHASE_SHOW = 'subs_purchase_show',
  PAYMENT_METHOD_SELECTED = 'payment_method_selected',
  PAYMENT_METHOD_POPUP_TAP = 'payment_method_popup_tap',
  PAYMENT_FINAL_POPUP_TAP = 'payment_final_popup_tap',
  PAYMENT_METHOD_TAP = 'change_payment_method_tap',
  PURCHASE_STARTED = 'subs_purchase_started',
  PURCHASE_STARTED_POPUP = 'subs_purchase_started_popup',
  PURCHASE_COMPLETED = 'subs_purchase_completed',
  PURCHASE_FAILED = 'subs_purchase_failed',
  PURCHASE_SCREEN_CLOSED = 'subs_purchase_screen_close',
  FINISH_ACCOUNT_SCREEN_VIEW = 'finish_account_screen_view',
  DOWNLOAD_BTN_TAP = 'download_button_tap',
  DOWNLOAD_FILE_BTN_TAP = 'download_pdf_guide_click',
  PAYPAL_PAYMENT_POPUP_CLOSE = 'paypal_payment_popup_close',
  GENERATION_PLAN_SCREEN = 'generation_plan_screen',
  PLAN_PREPARATION_SCREEN_SHOW = 'plan_preparation_screen_show',
  PLAN_PREPARATION_SCREEN_FINISHED = 'plan_preparation_screen_finished',
  EMAIL_PAGE_COMPLETED = 'email_page_completed',
  EMAIL_PAGE_ERROR = 'email_page_error',
  EMAIL_PAGE_SHOW = 'email_page_show',
  PLANS_PAGE_SCROLLED = 'plans_page_scrolled',
  PLANS_PAGE_BUTTON_TAP = 'plans_page_button_tap',
  PLANS_PAGE_TIMER_EXPIRED = 'plans_page_timer_expired',
  CHOOSE_TRIAL_PRICE_PAGE_SHOWN = 'choose_trial_price_page_show',
  CHOOSE_TRIAL_PRICE_PAGE_COMPLETED = 'choose_trial_price_page_completed',
  QUESTION_POPUP_TAP = 'question_popup_tap',
  SCRATCH_DISCOUNT_PAGE_SHOW = 'scratch_discount_page_show',
  SCRATCHING_STARTED = 'scratching_started',
  SCRATCHING_FINISHED = 'scratching_finished',
  PRE_CHECKOUT_MODAL_SHOW = 'pre_checkout_modal_show',
  PRE_CHECKOUT_MODAL_CLOSE = 'pre_checkout_modal_close',
  DISCOUNT_POPUP = 'discount_popup',
  DISCOUNT_POPUP_BUTTON_TAP = 'discount_popup_button_tap',
  PRE_CANCEL_OFFER_MODAL_SHOW = 'pre_cancel_offer_modal_show',
  CANCEL_OFFER_PAGE_SHOW = 'cancel_offer_page_show',
  CANCEL_OFFER_PAGE_CLOSE = 'cancel_offer_page_close',
  UPSELL_CANCEL_OFFER_QUESTION_SHOW = 'upsell_cancel_offer_question_page_show',
  UPSELL_CANCEL_OFFER_QUESTION_CLOSE = 'upsell_cancel_offer_question_page_close',
  UPSELL_PLANS_PAGE_SHOW = 'upsell_plans_page_show',
  UPSELL_PLANS_PAGE_SKIP = 'upsell_plans_page_skip',
  UPSELL_PLANS_BUTTON_TAP = 'upsell_plans_page_button_tap',
  UPSELL_PROGRAM_TOGGLE_ENABLE = 'upsell_program_toggle_enable',
  UPSELL_PROGRAM_TOGGLE_DISABLE = 'upsell_program_toggle_disable',
  INAPP_PURCHASE_STARTED = 'inapp_purchase_started',
  INAPP_PURCHASE_COMPLETED = 'inapp_purchase_completed',
  INAPP_PURCHASE_FAILED = 'inapp_purchase_failed',
  UPSELL_PLANS_PAGE_SCROLL = 'upsell_plans_page_scrolled',
  CREATE_LEARN_WORLDS_ACCOUNT_SHOW = 'create_learn_worlds_account_page',
  CREATE_ACCOUNT_SHOW = 'create_account_page',
  ACCOUNT_CREATED = 'account_created',
  ACCOUNT_CREATION_FAILED = 'account_creation_failed',
  LEARN_WORLDS_ACCOUNT_CREATED = 'learn_worlds_account_created',
  LEARN_WORLDS_ACCOUNT_CREATION_FAILED = 'learn_worlds_account_creation_failed',
  ACCOUNT_CREATED_MODAL_SHOW = 'account_created_modal_show',
  ACCOUNT_CREATED_MODAL_CLOSE = 'account_created_modal_close',
  EMAIL_EXISTS_POPUP_CLOSE = 'email_exists_popup_close',
  EMAIL_EXISTS_POPUP_SHOW = 'email_exists_popup_show',
  CHECK_YOUR_INBOX_SCREEN_SHOW = 'check_your_inbox_screen_show',
  CHECK_YOUR_INBOX_CONTINUE_TAP = 'check_your_inbox_continue_tap',
  CONTACT_US = 'contact_us',
  COOKIES_BANNER_SHOW = 'cookies_banner_show',
  COOKIES_BANNER_SETTINGS_TAP = 'cookies_banner_settings_tap',
  COOKIES_ACCEPT_ALL_TAP = 'cookies_accept_all_tap',
  COOKIES_REJECT_ALL_TAP = 'cookies_reject_all_tap',
  COOKIES_SETTINGS_TOGGLE_ENABLE = 'cookies_settings_toggle_enable',
  COOKIES_SETTINGS_TOGGLE_DISABLE = 'cookies_settings_toggle_disable',
  COMMITMENT_PROMISE_SHOW = 'commitment_promise_page_show',
  COMMITMENT_PROMISE_FIRST_HOLD = 'commitment_promise_first_hold',
  HOW_OTHERS_ANSWERED_SHOWED = 'how_others_answered_showed',
  BREAK_SCREEN_COUNTER_TAP = 'break_screen_counter_tap',
  CTA_BUTTON_TAP = 'cta_button_tap',
}

class EventLoggerService {
  private loggers?: Map<EventLoggerInstanceName, IEventLogger>
  private eventsQueue: IEvent[] = []
  private eventsCache: Map<Events, string> = new Map()
  private pendingAmplitudeEvents: IEvent[] = []
  private isAmplitudeBufferingStopped = false

  get isAmplitudeActive() {
    return this.loggers?.has(EventLoggerInstanceName.AMPLITUDE)
  }

  get isGIAActive() {
    return this.loggers?.has(EventLoggerInstanceName.GIA)
  }

  init(...loggers: IEventLogger[]): void {
    const entriesArr = loggers.map(
      (logger) =>
        [logger.name, logger] as [EventLoggerInstanceName, IEventLogger],
    )

    if (!this.loggers) {
      this.loggers = new Map(entriesArr)
      this.notifyInitFinished()
      this.sendPendingAmplitudeEvents()

      return
    }

    if (this.loggers) {
      loggers.map((logger) =>
        this.loggers?.set(
          logger.name as EventLoggerInstanceName,
          logger as IEventLogger,
        ),
      )
    }

    this.notifyInitFinished()
    this.sendPendingAmplitudeEvents()
  }

  stopBufferingAmplitudeEvents(): void {
    this.isAmplitudeBufferingStopped = true
    this.pendingAmplitudeEvents = []
  }

  logSessionStarted(linkLocale: string): void {
    const event = Events.SESSION_STARTED
    const eventProperty = {
      browser_locale_info: navigator.language,
      link_locale_info: linkLocale,
      device_type: getMobileOperatingSystem(),
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logFirstPageShown(
    screenName: string,
    firstScreenReadyEvent: Record<string, unknown> | null,
  ): void {
    const eventProperty = { screen_name: screenName }

    this.logEventOrPushToQueue(Events.FIRST_PAGE_SHOW, eventProperty, false)
    this.logAmplitudeOnlyEvent(Events.FIRST_PAGE_SHOW, {
      ...eventProperty,
      ...firstScreenReadyEvent,
    })
  }

  logFirstPageCompleted({ question, answers }: TQuestionPageParams): void {
    const eventProperty = {
      question,
      answer: Array.isArray(answers) ? answers.join(',') : answers,
    }
    this.logEventOrPushToQueue(Events.FIRST_PAGE_COMPLETED, eventProperty)
  }

  logGrowthBookAbSegmentName({
    variantId,
    experimentKey,
    variantName,
  }: {
    experimentKey: string
    variantId: string
    variantName: string
  }): void {
    const event = Events.AB_SEGMENT

    const eventProperty = {
      growthbook_feature_key: experimentKey || 'experiment_id_not_set',
      ab_variant: getCurrentOnboardingVariant(variantId),
      ab_segment_name: variantName || 'no_name',
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logBiUserAge(age: string): void {
    const event = Events.BI_USER_AGE
    const eventProperty = { answer: age }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logBiUserGender(gender: string): void {
    const event = Events.BI_USER_GENDER
    const eventProperty = { answer: gender }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logGenerationPlanScreen(): void {
    const event = Events.GENERATION_PLAN_SCREEN
    this.logEventOrPushToQueue(event, {}, false)
  }

  logPlanPreparationScreen(): void {
    const event = Events.PLAN_PREPARATION_SCREEN_SHOW
    this.logEventOrPushToQueue(event, {}, false)
  }

  logPlanPreparationScreenFinished(): void {
    const event = Events.PLAN_PREPARATION_SCREEN_FINISHED
    this.logEventOrPushToQueue(event, {}, false)
  }

  logEmailPageShow() {
    const event = Events.EMAIL_PAGE_SHOW
    this.logEventOrPushToQueue(event)
  }

  logEmailPageCompleted(email: string): void {
    const event = Events.EMAIL_PAGE_COMPLETED
    const eventProperty = {
      email,
    }

    this.logEventOrPushToQueue(event, eventProperty)
  }

  logEmailPageError(error: string): void {
    const event = Events.EMAIL_PAGE_ERROR
    const eventProperty = {
      error,
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logPlanPageViewScroll({
    screenName,
    scrolledPart,
  }: {
    screenName: ScreenName
    scrolledPart: number
  }): void {
    const event = Events.PLANS_PAGE_SCROLLED
    const eventProperty = {
      screen_name: screenName,
      scrolled_part: `${scrolledPart}%`,
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logPlanPageButtonClicked({
    screenName,
    buttonNumber,
    buttonText,
  }: {
    screenName: ScreenName
    buttonNumber: number
    buttonText: string
  }): void {
    const event = Events.PLANS_PAGE_BUTTON_TAP
    const eventProperty = {
      screen_name: screenName,
      button_number: buttonNumber,
      button_text: buttonText,
    }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logPlanPageTimerExpired(pageId: string): void {
    const event = Events.PLANS_PAGE_TIMER_EXPIRED
    const eventProperty = {
      page_id: pageId,
    }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logQuestion({ question, answers, skipScreen }: TQuestionPageParams): void {
    const event = Events.QUESTION_COMPLETED
    const eventProperty = {
      question,
      ...(answers && {
        answer: Array.isArray(answers) ? answers.join(',') : answers,
      }),
      ...(skipScreen && { skipStep: skipScreen }),
    }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logCommitmentPromiseShown(): void {
    const event = Events.COMMITMENT_PROMISE_SHOW
    this.logEventOrPushToQueue(event, {}, false)
  }

  logCommitmentPromiseFirstHold(): void {
    const event = Events.COMMITMENT_PROMISE_FIRST_HOLD
    this.logEventOrPushToQueue(event, {}, false)
  }

  logHowOthersAnsweredShowed({ question, answers }: TQuestionPageParams): void {
    const event = Events.HOW_OTHERS_ANSWERED_SHOWED
    const eventProperty = { question, answer: answers }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logBreakScreenCounterTap({
    screen,
    tap,
  }: {
    screen: string
    tap: number
  }): void {
    const event = Events.BREAK_SCREEN_COUNTER_TAP
    const eventProperty = { screen, tap }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logCtaButtonTap({
    buttonText,
    screen,
  }: {
    buttonText: string
    screen: string
  }): void {
    const event = Events.CTA_BUTTON_TAP
    const eventProperty = { button_text: buttonText, screen }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logQuestionPopupTap({ question, answers }: TQuestionPageParams): void {
    const event = Events.QUESTION_POPUP_TAP
    const eventProperty = {
      question,
      ...(answers && {
        answer: Array.isArray(answers) ? answers.join(',') : answers,
      }),
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logPayPalPaymentPopupClose(): void {
    const event = Events.PAYPAL_PAYMENT_POPUP_CLOSE
    this.logEventOrPushToQueue(event, {})
  }

  // Sale Page Events
  logSalePageShown({
    productIds,
    screenName = ScreenName.PURCHASE,
  }: {
    productIds: string[]
    screenName?: ScreenName
  }): void {
    const lastLoggedScreen = this.eventsCache.get(Events.SALE_SCREEN_SHOW)

    if (lastLoggedScreen !== screenName) {
      const eventProperty = {
        product_id: productIds.join(','),
        screen_name: screenName,
      }

      this.eventsCache.set(Events.SALE_SCREEN_SHOW, screenName)
      this.logEventOrPushToQueue(Events.SALE_SCREEN_SHOW, eventProperty)
    }
  }

  logScratchDiscountPageShown(): void {
    const event = Events.SCRATCH_DISCOUNT_PAGE_SHOW

    this.logEventOrPushToQueue(event, {}, false)
  }

  logScratchingStarted(): void {
    const event = Events.SCRATCHING_STARTED

    this.logEventOrPushToQueue(event, {}, false)
  }

  logScratchingFinished(): void {
    const event = Events.SCRATCHING_FINISHED

    this.logEventOrPushToQueue(event, {}, false)
  }

  logChooseTrialPricePageShown(): void {
    const event = Events.CHOOSE_TRIAL_PRICE_PAGE_SHOWN

    this.logEventOrPushToQueue(event, {})
  }

  logChooseTrialPricePageCompleted({
    trialPrice,
    currency,
  }: {
    trialPrice: number
    currency: string
  }): void {
    const event = Events.CHOOSE_TRIAL_PRICE_PAGE_COMPLETED
    const eventProperty = {
      trial_price: trialPrice,
      currency,
    }

    this.logEventOrPushToQueue(event, eventProperty)
  }

  logPreCheckoutModalShown(): void {
    const event = Events.PRE_CHECKOUT_MODAL_SHOW
    this.logEventOrPushToQueue(event, {}, true)
  }

  logPreCheckoutModalClose(): void {
    const event = Events.PRE_CHECKOUT_MODAL_CLOSE
    this.logEventOrPushToQueue(event, {}, true)
  }

  logDiscountPopup(state: string): void {
    this.logEventOrPushToQueue(Events.DISCOUNT_POPUP, { state }, true)
  }

  logDiscountPopupButtonTap(button: string): void {
    this.logEventOrPushToQueue(
      Events.DISCOUNT_POPUP_BUTTON_TAP,
      { button },
      true,
    )
  }

  logPreCancelOfferModalShown(): void {
    const event = Events.PRE_CANCEL_OFFER_MODAL_SHOW
    this.logEventOrPushToQueue(event, {}, true)
  }

  logCancelOfferPageShown(): void {
    const event = Events.CANCEL_OFFER_PAGE_SHOW
    this.logEventOrPushToQueue(event, {}, true)
  }

  logCancelOfferPageClosed(): void {
    const event = Events.CANCEL_OFFER_PAGE_CLOSE
    this.logEventOrPushToQueue(event, {}, false)
  }

  logUpsellCancelOfferQuestionShown(): void {
    const event = Events.UPSELL_CANCEL_OFFER_QUESTION_SHOW
    this.logEventOrPushToQueue(event, {}, false)
  }

  logUpsellCancelOfferQuestionClose(buttonText: string): void {
    const event = Events.UPSELL_CANCEL_OFFER_QUESTION_CLOSE
    const eventProperty = {
      tap: buttonText,
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logTermsOfUseClicked(): void {
    const event = Events.TERMS_OF_USE
    this.logEventOrPushToQueue(event, {}, false)
  }

  logPrivacyPolicyClicked(): void {
    const event = Events.PRIVACY_POLICY
    this.logEventOrPushToQueue(event, {}, false)
  }

  logSubscriptionConsentAccepted({
    productId,
    priceLookupKey,
    trialPriceId,
    trialPeriodDays,
    periodName,
    periodQuantity,
    currency,
    screenName,
  }: {
    productId: TProductId
    priceLookupKey: string
    trialPriceId?: string
    trialPeriodDays: number
    periodName: string | null
    periodQuantity: number
    currency: string
    screenName: ScreenName
  }): void {
    const event = Events.SUBSCRIPTION_CONSENT_ACCEPTED
    const eventProperty = {
      product_id: productId,
      subscription_lookup_key: priceLookupKey,
      ...(trialPriceId && { trial_lookup_key: trialPriceId }),
      trial_period_days: trialPeriodDays,
      period_name: periodName,
      period_quantity: periodQuantity,
      currency,
      screen_name: screenName,
    }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logCookiePolicyClicked(): void {
    const event = Events.COOKIE_POLICY_TAP
    this.logEventOrPushToQueue(event, {}, false)
  }

  // Getting App Page Events
  logGettingAppShown(): void {
    const event = Events.FINISH_ACCOUNT_SCREEN_VIEW
    this.logEventOrPushToQueue(event, {})
  }

  logDownloadTap(): void {
    const event = Events.DOWNLOAD_BTN_TAP

    this.logEventOrPushToQueue(event, {}, true)
  }

  logDownloadFileTap(fileName: string): void {
    const event = Events.DOWNLOAD_FILE_BTN_TAP

    this.logEventOrPushToQueue(event, { file_name: fileName })
  }

  // Purchase Page events
  logPurchaseShown({
    stripeAccountId,
    stripeAccountName,
    productId,
    screenName = ScreenName.PURCHASE,
  }: {
    stripeAccountId: string
    stripeAccountName: string
    productId: TProductId
    screenName?: ScreenName
  }): void {
    const event = Events.PURCHASE_SHOW
    const eventProperty = {
      screen_name: screenName,
      stripe_account_id: stripeAccountId,
      stripe_account_name: stripeAccountName,
      product_id: productId,
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logPaymentMethodSelected({
    paymentMethod,
    sourceStep = SourceStep.CHECKOUT,
    screenName = ScreenName.PURCHASE,
  }: {
    paymentMethod: PaymentMethod
    sourceStep?: SourceStep
    screenName?: ScreenName
  }): void {
    const event = Events.PAYMENT_METHOD_SELECTED
    const eventProperty = {
      payment_method: paymentMethod,
      source_step: sourceStep,
      screen_name: screenName,
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logPaymentMethodPopupTap(): void {
    const event = Events.PAYMENT_METHOD_POPUP_TAP
    this.logEventOrPushToQueue(event, {}, false)
  }

  logPaymentFinalMethodPopupTap(buttonText: string): void {
    const event = Events.PAYMENT_FINAL_POPUP_TAP
    const eventProperty = {
      button_text: buttonText,
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logPaymentMethodTap({
    paymentMethod,
  }: {
    paymentMethod: PaymentMethod
  }): void {
    const event = Events.PAYMENT_METHOD_TAP

    const eventProperty = {
      payment_method: paymentMethod,
    }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logPurchaseStarted({
    eventName = Events.PURCHASE_STARTED,
    productId,
    productKey,
    priceDetails: { price, trial = false, currency },
    paymentMethod,
    paymentSystem,
    screenName,
    stripeAccountName,
    stripeAccountId,
    isUpgraded = false,
    amountToPay,
    sourceStep = SourceStep.CHECKOUT,
    processorName,
  }: {
    eventName?: Events
    productId: TProductId
    productKey?: string
    priceDetails: { price: number; trial?: boolean; currency: string }
    paymentMethod: PaymentMethod
    paymentSystem?: PaymentSystem
    screenName: ScreenName
    stripeAccountName: string
    stripeAccountId: string
    isUpgraded?: boolean
    amountToPay?: string
    sourceStep?: SourceStep
    processorName?: string
  }): void {
    const eventProperty = {
      trial,
      price,
      currency,
      product_id: productId,
      ...(productKey && { product_key: productKey }),
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      screen_name: screenName,
      source_step: sourceStep,
      stripe_account_name: stripeAccountName,
      stripe_account_id: stripeAccountId,
      ...(paymentSystem && {
        payment_system: paymentSystem,
      }),
      ...(paymentMethod === PaymentMethod.CREDIT_CARD &&
        processorName && { payment_processor: processorName }),
      ...(isUpgraded && {
        is_upgraded: isUpgraded,
        amount_to_pay: amountToPay,
      }),
    }

    this.logEventOrPushToQueue(eventName, eventProperty)
  }

  logPurchaseCompleted({
    productId,
    priceDetails: {
      price,
      periodQuantity,
      periodName,
      trial,
      trialPrice,
      trialPeriodDays,
      currency,
    },
    paymentMethod,
    paymentSystem,
    discountApplied,
    screenName,
    stripeAccountName,
    stripeAccountId,
    utmSource,
    isUpgraded = false,
    amountToPay,
    processorName,
    purchaseType,
  }: {
    productId: TProductId
    priceDetails: {
      price: number
      periodQuantity: number | null
      periodName: TimeInterval | null
      trial: boolean
      trialPrice: number
      trialPeriodDays: number
      currency: string
    }
    paymentMethod?: PaymentMethod
    discountApplied?: string
    screenName: ScreenName
    stripeAccountName: string
    stripeAccountId: string
    utmSource?: string
    isUpgraded?: boolean
    amountToPay?: string
    paymentSystem?: PaymentSystem
    processorName?: string
    purchaseType: SubscriptionListType
  }): void {
    const event = Events.PURCHASE_COMPLETED
    const eventProperty = {
      trial,
      price,
      currency,
      product_id: productId,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      screen_name: screenName,
      stripe_account_name: stripeAccountName,
      stripe_account_id: stripeAccountId,
      is_first_purchase: purchaseType === SubscriptionListType.PURCHASE,
      purchase_type: purchaseType,
      plan_type: `${trialPeriodDays}d+${periodQuantity}_${periodName}`,
      plan_price: `${trialPrice}+${price}_${currency}`,
      ...(paymentSystem && {
        payment_system: paymentSystem,
      }),
      ...(paymentMethod === PaymentMethod.CREDIT_CARD &&
        processorName && { payment_processor: processorName }),
      ...(discountApplied && { discount_applied: discountApplied }),
      ...(utmSource && { utm_source: utmSource }),
      ...(isUpgraded && {
        is_upgraded: isUpgraded,
        amount_to_pay: amountToPay,
      }),
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logPurchaseFailed({
    productId,
    productKey,
    priceDetails: { price, trial = false, currency },
    error: { description, type, code },
    paymentMethod,
    screenName,
    isUpgraded = false,
    amountToPay,
    stripeAccountName,
    stripeAccountId,
    paymentSystem,
    processorName,
  }: {
    productId: TProductId
    productKey?: string
    priceDetails: { price: number; trial?: boolean; currency: string }
    error: { type: string; description?: string; code?: string }
    paymentMethod?: PaymentMethod
    screenName: ScreenName
    isUpgraded?: boolean
    amountToPay?: string
    stripeAccountName: string
    stripeAccountId: string
    paymentSystem?: PaymentSystem
    processorName?: string
  }): void {
    const event = Events.PURCHASE_FAILED
    const eventProperty = {
      trial,
      price,
      currency,
      error_type: type,
      ...(description && { error_description: description }),
      ...(code && { error_code: code }),
      ...(paymentSystem && { payment_system: paymentSystem }),
      product_id: productId,
      ...(productKey && { product_key: productKey }),
      screen_name: screenName,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      ...(paymentMethod === PaymentMethod.CREDIT_CARD &&
        processorName && { payment_processor: processorName }),
      stripe_account_name: stripeAccountName,
      stripe_account_id: stripeAccountId,
      ...(isUpgraded && {
        is_upgraded: isUpgraded,
        amount_to_pay: amountToPay,
      }),
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logPurchaseScreenClosed({
    productId,
    screenName,
  }: {
    productId: TProductId
    screenName: ScreenName
  }): void {
    const event = Events.PURCHASE_SCREEN_CLOSED
    const eventProperty = {
      product_id: productId,
      screen_name: screenName,
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logCreateLearnWorldsAccountShown(): void {
    const event = Events.CREATE_LEARN_WORLDS_ACCOUNT_SHOW

    this.logEventOrPushToQueue(event, {})
  }

  logCreateAccountShown(): void {
    const event = Events.CREATE_ACCOUNT_SHOW

    this.logEventOrPushToQueue(event, {})
  }

  logLearnWorldsAccountCreated(email: string): void {
    const event = Events.LEARN_WORLDS_ACCOUNT_CREATED
    this.logEventOrPushToQueue(event, { email })
  }

  logAccountCreated(email: string): void {
    const event = Events.ACCOUNT_CREATED
    this.logEventOrPushToQueue(event, { email })
  }

  logAccountCreationFailed(error: string): void {
    const event = Events.ACCOUNT_CREATION_FAILED
    const eventProperty = {
      error_reason: error,
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logLearnWorldsAccountCreationFailed(error: string): void {
    const event = Events.LEARN_WORLDS_ACCOUNT_CREATION_FAILED
    const eventProperty = {
      error_reason: error,
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logAccountCreatedModalShown(): void {
    const event = Events.ACCOUNT_CREATED_MODAL_SHOW
    this.logEventOrPushToQueue(event, {})
  }

  logAccountCreatedModalClosed(): void {
    const event = Events.ACCOUNT_CREATED_MODAL_CLOSE
    this.logEventOrPushToQueue(event, {})
  }

  logUpsellPurchaseShown(screenName: ScreenName): void {
    const event = Events.UPSELL_PLANS_PAGE_SHOW
    const eventProperty = {
      screen_name: screenName,
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logUpsellPurchaseSkipped(screenName: ScreenName): void {
    const event = Events.UPSELL_PLANS_PAGE_SKIP
    const eventProperty = {
      screen_name: screenName,
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logUpsellPlansButtonTap({
    screenName,
    buttonText,
    selectedPlans,
  }: {
    screenName: ScreenName
    buttonText: string
    selectedPlans: string | string[]
  }): void {
    const event = Events.UPSELL_PLANS_BUTTON_TAP
    const eventProperty = {
      screen_name: screenName,
      button_text: buttonText,
      selected_plans: selectedPlans,
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logUpsellSubscriptionEnable({
    upsellName,
    screenName,
  }: {
    upsellName: string
    screenName: string
  }) {
    const event = Events.UPSELL_PROGRAM_TOGGLE_ENABLE
    const eventProperty = {
      upsell_name: upsellName,
      screen_name: screenName,
    }

    this.logEventOrPushToQueue(event, eventProperty)
  }

  logUpsellSubscriptionDisable({
    upsellName,
    screenName,
  }: {
    upsellName: string
    screenName: string
  }) {
    const event = Events.UPSELL_PROGRAM_TOGGLE_DISABLE
    const eventProperty = {
      upsell_name: upsellName,
      screen_name: screenName,
    }

    this.logEventOrPushToQueue(event, eventProperty)
  }

  logContactUsClicked(): void {
    const event = Events.CONTACT_US

    this.logEventOrPushToQueue(event, {}, false)
  }

  logUpsellPageViewScroll({
    screenName,
    scrolledPart,
  }: {
    screenName: ScreenName
    scrolledPart: number
  }): void {
    const event = Events.UPSELL_PLANS_PAGE_SCROLL
    const eventProperty = {
      screen_name: screenName,
      scrolled_part: `${scrolledPart}%`,
    }
    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logInAppPurchaseStarted({
    productId,
    price,
    currency = 'USD',
    paymentMethod,
    screenName,
    paymentSystem,
    stripeAccountId,
    stripeAccountName,
    processorName,
  }: {
    productId: TProductId
    price: number
    currency: string
    paymentMethod: PaymentMethod
    screenName: ScreenName
    paymentSystem: PaymentSystem
    stripeAccountId: string
    stripeAccountName: string
    processorName?: string
  }): void {
    const event = Events.INAPP_PURCHASE_STARTED
    const eventProperty = {
      price,
      currency,
      product_id: productId,
      screen_name: screenName,
      payment_system: paymentSystem,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      stripe_account_name: stripeAccountName,
      stripe_account_id: stripeAccountId,
      ...(processorName && { payment_processor: processorName }),
    }

    this.logEventOrPushToQueue(event, eventProperty)
  }

  logInAppPurchaseCompleted({
    productId,
    price,
    currency = 'USD',
    paymentMethod,
    screenName,
    paymentSystem,
    stripeAccountId,
    stripeAccountName,
    processorName,
  }: {
    productId: TProductId
    price: number
    currency: string
    paymentMethod: PaymentMethod
    screenName: ScreenName
    paymentSystem: PaymentSystem
    stripeAccountId: string
    stripeAccountName: string
    processorName?: string
  }): void {
    const event = Events.INAPP_PURCHASE_COMPLETED
    const eventProperty = {
      price,
      currency,
      product_id: productId,
      screen_name: screenName,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      payment_system: paymentSystem,
      stripe_account_name: stripeAccountName,
      stripe_account_id: stripeAccountId,
      ...(processorName && { payment_processor: processorName }),
    }

    this.logEventOrPushToQueue(event, eventProperty)
  }

  logInAppPurchaseFailed({
    productId,
    price,
    currency = 'USD',
    paymentMethod,
    screenName,
    paymentSystem,
    error: { description, type, code },
    stripeAccountId,
    stripeAccountName,
    processorName,
  }: {
    productId: TProductId
    price: number
    currency: string
    paymentMethod: PaymentMethod
    screenName: ScreenName
    paymentSystem: PaymentSystem
    error: { type: string; description?: string; code?: string }
    stripeAccountId: string
    stripeAccountName: string
    processorName?: string
  }): void {
    const event = Events.INAPP_PURCHASE_FAILED
    const eventProperty = {
      price,
      currency,
      product_id: productId,
      screen_name: screenName,
      error_type: type,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      payment_system: paymentSystem,
      stripe_account_name: stripeAccountName,
      stripe_account_id: stripeAccountId,
      ...(code && { error_code: code }),
      ...(description && { error_description: description }),
      ...(processorName && { payment_processor: processorName }),
    }

    this.logEventOrPushToQueue(event, eventProperty)
  }

  logCookiesConsentShown(): void {
    const event = Events.COOKIES_BANNER_SHOW
    this.logEventOrPushToQueue(event, {}, false)
  }

  logEmailExistsPopupShow(email: string): void {
    const event = Events.EMAIL_EXISTS_POPUP_SHOW

    this.logEventOrPushToQueue(event, { email })
  }

  logEmailExistsPopupClose(email: string, tap: string): void {
    const event = Events.EMAIL_EXISTS_POPUP_CLOSE

    this.logEventOrPushToQueue(event, { email, tap })
  }

  logCheckYourInboxScreenShow(): void {
    const event = Events.CHECK_YOUR_INBOX_SCREEN_SHOW

    this.logEventOrPushToQueue(event)
  }

  logCheckYourInboxContinueTap(): void {
    const event = Events.CHECK_YOUR_INBOX_CONTINUE_TAP

    this.logEventOrPushToQueue(event)
  }

  logCookiesConsentSettingsTap(): void {
    const event = Events.COOKIES_BANNER_SETTINGS_TAP
    this.logEventOrPushToQueue(event, {}, false)
  }

  logCookiesConsentAcceptAllTap(source: string): void {
    const event = Events.COOKIES_ACCEPT_ALL_TAP
    const eventProperty = { source }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logCookiesConsentRejectAllTap(source: string): void {
    const event = Events.COOKIES_REJECT_ALL_TAP
    const eventProperty = { source }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logCookiesConsentToggleEnable(option: string): void {
    const event = Events.COOKIES_SETTINGS_TOGGLE_ENABLE
    const eventProperty = { option }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  logCookiesConsentToggleDisable(option: string): void {
    const event = Events.COOKIES_SETTINGS_TOGGLE_DISABLE
    const eventProperty = { option }

    this.logEventOrPushToQueue(event, eventProperty, false)
  }

  private logEventOrPushToQueue(
    event: Events,
    eventProperty?: Record<string, any>,
    isAmplitudeEvent = true,
    callback?: () => void,
  ): void {
    if (this.isGIAActive) {
      this.logEvent({
        event,
        isAmplitudeEvent,
        eventProperty,
        callback,
      })
    } else {
      this.eventsQueue.push({
        event,
        eventProperty,
        isAmplitudeEvent,
      })
    }
  }

  private notifyInitFinished() {
    if (this.eventsQueue.length) {
      this.eventsQueue.forEach(({ event, eventProperty, isAmplitudeEvent }) =>
        this.logEvent({
          event,
          eventProperty,
          isAmplitudeEvent,
        }),
      )
      this.eventsQueue = []
    }
  }

  private sendPendingAmplitudeEvents(): void {
    if (!this.isAmplitudeActive || !this.pendingAmplitudeEvents.length) {
      return
    }

    const amplitudeLogger = this.loggers?.get(EventLoggerInstanceName.AMPLITUDE)
    const eventsToSend = this.pendingAmplitudeEvents

    this.pendingAmplitudeEvents = []

    eventsToSend.forEach(({ event, eventProperty }) => {
      try {
        amplitudeLogger?.log(event, eventProperty)
      } catch {
        // flushing the buffer must not break init() and the other events
      }
    })
  }

  private logAmplitudeOnlyEvent(
    event: Events,
    eventProperty: Record<string, unknown>,
  ): void {
    if (this.isAmplitudeActive) {
      this.loggers
        ?.get(EventLoggerInstanceName.AMPLITUDE)
        ?.log(event, eventProperty)
      return
    }

    if (!this.isAmplitudeBufferingStopped) {
      this.pendingAmplitudeEvents.push({ event, eventProperty })
    }
  }

  private logEvent({
    event,
    eventProperty,
    isAmplitudeEvent = true,
    callback,
  }: {
    event: Events
    eventProperty?: Record<string, any>
    isAmplitudeEvent?: boolean
    callback?: () => void
  }): void {
    if (!isAmplitudeEvent) {
      this.loggers
        ?.get(EventLoggerInstanceName.GIA)
        ?.log(event, eventProperty, callback)

      this.loggers
        ?.get(EventLoggerInstanceName.USER_FLOW_TRACKER)
        ?.log(event, eventProperty, callback)
      return
    }

    if (!this.isAmplitudeActive && !this.isAmplitudeBufferingStopped) {
      this.pendingAmplitudeEvents.push({ event, eventProperty })
    }

    if (this.loggers) {
      const eventLoggers = [...this.loggers.values()]

      eventLoggers.forEach((logger, index, array) => {
        index === array.length - 1
          ? logger.log(event, eventProperty, callback)
          : logger.log(event, eventProperty)
      })
    }
  }
}

export const eventLogger = new EventLoggerService()
